/**
 * Le wikitexte de l'onglet « Modifier le code », et la barre d'insertion.
 *
 * Rien ici ne touche au DOM : l'insertion est une opération sur une chaîne et
 * une position de curseur, ce qui se vérifie sans navigateur — et c'est
 * exactement ce qui se casse sans qu'on le voie (un balisage inséré au mauvais
 * endroit, une sélection perdue, un curseur qui ne se replace pas entre les
 * deux apostrophes).
 */

import type { Leaf, Locale } from '../../i18n/strings';
import { S } from '../../i18n/strings';

/**
 * Les trois commentaires cachés du wikitexte.
 *
 * **Ils se conservent mot pour mot.** C'est eux que le visiteur curieux
 * découvre en ouvrant l'onglet, et ils racontent la guerre d'édition que
 * l'historique donne par ailleurs. Les reformuler, c'est perdre la blague.
 */
const HIDDEN = {
  fr: [
    "<!-- « LE poisson Steve ». Jamais « un ». Il n'y en a qu'un. -->",
    '<!-- NE PAS RETIRER LES BRAS. Voir discussion, section 4. -->',
    '<!-- @Anonyme_88 : arrête. -->',
  ],
  en: [
    '<!-- "THE fish Steve". Never "a". There is only one. -->',
    '<!-- DO NOT REMOVE THE ARMS. See talk, section 4. -->',
    '<!-- @Anon_88: stop. -->',
  ],
} as const;

/** Les trois commentaires cachés, pour les tests et pour la relecture. */
export function hiddenComments(lang: Locale): readonly string[] {
  return HIDDEN[lang];
}

/**
 * Le wikitexte pré-rempli.
 *
 * La **syntaxe ne se traduit pas** (`{{Infobox`, `[[Catégorie:`, `<ref>`) : ce
 * sont des noms de modèles, pas de la copie. Seules les valeurs et les
 * commentaires changent de langue.
 */
export function initialWikitext(lang: Locale): string {
  const [one, two, three] = HIDDEN[lang];
  if (lang === 'en') {
    return [
      '{{Infobox Fish',
      '| name           = Steve the fish',
      '| binomial       = Steveus orangeus',
      '| individuals    = 1',
      '| colour         = orange',
      '| arms           = 2',
      '| legs           = 2',
      '| eyes           = 2',
      '| dorsal         = none',
      '| beauty         = really very great',
      '| smell          = necessarily bad',
      '| on land        = swims',
      '| in water       = walks',
      '| status         = WLH',
      '}}',
      '',
      '{{Single source}}',
      '{{Neutrality}}',
      '{{Unverifiable}}',
      '',
      "'''Steve the fish''' (''Steveus orangeus'') is an orange fish with arms",
      'and legs, which appeared on the Internet in April 2025<ref name="song" />.',
      '',
      one,
      '',
      'He can swim on land and walk in water<ref name="song" />.',
      '{{citation needed}}',
      '',
      '== Etymology ==',
      'He is called Steve. Nobody knows why.',
      '',
      '== Description ==',
      '=== Arms and legs ===',
      two,
      '=== Colour ===',
      'He is orange.',
      three,
      '=== Eyes ===',
      "He has '''two''' eyes. They are large.",
      '=== Beauty ===',
      'He is really very beautiful.',
      '',
      '== Locomotion ==',
      '{{Main|Terrestrial swimming|Aquatic walking}}',
      '',
      '== Smell ==',
      'Necessarily bad. No volunteer to verify.',
      '',
      '== The song ==',
      '[[File:Steve_the_fish_song.mp3]]',
      '<!-- single source for the whole article. do not delete. -->',
      '',
      '<references>',
      '<ref name="song">The song, April 2025, 30 s.</ref>',
      '</references>',
      '',
      '[[Category:Fish]]',
      '[[Category:Fish with legs]]',
      '[[Category:Single-individual species]]',
      '[[Category:We like him]]',
    ].join('\n');
  }

  return [
    '{{Infobox Poisson',
    '| nom            = Le poisson Steve',
    '| nom binominal  = Steveus orangeus',
    '| individus      = 1',
    '| couleur        = orange',
    '| bras           = 2',
    '| jambes         = 2',
    '| yeux           = 2',
    '| dorsale        = aucune',
    '| beauté         = vraiment très grande',
    '| odeur          = forcément mauvaise',
    '| sur la terre   = nage',
    "| dans l'eau     = marche",
    '| statut         = OLAB',
    '}}',
    '',
    '{{Source unique}}',
    '{{Neutralité}}',
    '{{Invérifiable}}',
    '',
    "'''Le poisson Steve''' (''Steveus orangeus'') est un poisson orange doté de",
    'bras et de jambes, apparu sur Internet en avril 2025<ref name="chanson" />.',
    '',
    one,
    '',
    "Il peut nager sur la terre et marcher dans l'eau<ref name=\"chanson\" />.",
    '{{référence nécessaire}}',
    '',
    '== Étymologie ==',
    "Il s'appelle Steve. Personne ne sait pourquoi.",
    '',
    '== Description ==',
    '=== Bras et jambes ===',
    two,
    '=== Couleur ===',
    'Il est orange.',
    three,
    '=== Yeux ===',
    "Il a '''deux''' yeux. Ils sont grands.",
    '=== Beauté ===',
    'Il est vraiment très beau.',
    '',
    '== Locomotion ==',
    '{{Article détaillé|Nage terrestre|Marche aquatique}}',
    '',
    '== Odeur ==',
    'Forcément mauvaise. Aucun volontaire pour vérifier.',
    '',
    '== La chanson ==',
    '[[Fichier:Chanson_du_poisson_Steve.mp3]]',
    "<!-- source unique de tout l'article. ne pas supprimer. -->",
    '',
    '<references>',
    '<ref name="chanson">La chanson, avril 2025, 30 s.</ref>',
    '</references>',
    '',
    '[[Catégorie:Poisson]]',
    '[[Catégorie:Poisson à jambes]]',
    '[[Catégorie:Espèce à un seul individu]]',
    "[[Catégorie:On l'aime bien]]",
  ].join('\n');
}

/** Un bouton de la barre d'insertion : ce qu'il pose autour de la sélection. */
export interface Insert {
  readonly id: string;
  readonly label: Leaf;
  /** Ce qui s'écrit sur l'écran du bouton — le balisage lui-même. */
  readonly glyph: string;
  readonly before: string;
  readonly after: string;
  /** Texte posé quand rien n'est sélectionné. */
  readonly sample: string;
}

/** Les huit boutons de la barre, dans l'ordre de la vraie barre d'outils. */
export const INSERTS: readonly Insert[] = [
  { id: 'bold', label: S.wiki.editor.bold, glyph: "'''G'''", before: "'''", after: "'''", sample: 'gras' },
  { id: 'italic', label: S.wiki.editor.italic, glyph: "''I''", before: "''", after: "''", sample: 'italique' },
  { id: 'link', label: S.wiki.editor.link, glyph: '[[ ]]', before: '[[', after: ']]', sample: 'Lien' },
  { id: 'heading', label: S.wiki.editor.heading, glyph: '== ==', before: '== ', after: ' ==', sample: 'Titre' },
  { id: 'ref', label: S.wiki.editor.ref, glyph: '<ref>', before: '<ref>', after: '</ref>', sample: '' },
  { id: 'sign', label: S.wiki.editor.sign, glyph: '~~~~', before: '~~~~', after: '', sample: '' },
  { id: 'template', label: S.wiki.editor.template, glyph: '{{ }}', before: '{{', after: '}}', sample: 'Modèle' },
  { id: 'dash', label: S.wiki.editor.dash, glyph: '—', before: '—', after: '', sample: '' },
];

/** Ce que devient le texte, et où le curseur se replace. */
export interface Applied {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/**
 * Pose un balisage autour de la sélection.
 *
 * Deux cas, et c'est le second qui compte : sélection vide, on insère l'exemple
 * **et on le sélectionne**, pour que taper par-dessus le remplace. Sans ça le
 * curseur atterrit après `'''gras'''` et le visiteur écrit à côté de son propre
 * balisage.
 */
export function applyInsert(
  text: string,
  start: number,
  end: number,
  insert: Insert,
): Applied {
  const chosen = text.slice(start, end);
  const inner = chosen === '' ? insert.sample : chosen;
  const next = `${text.slice(0, start)}${insert.before}${inner}${insert.after}${text.slice(end)}`;
  const from = start + insert.before.length;
  return { text: next, start: from, end: from + inner.length };
}
